import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useChannelPartnerAuth } from '../../contexts/ChannelPartnerAuthContext';
import ThemeToggle from '../UI/ThemeToggle';
import {
  HiOutlineViewGrid,
  HiOutlineOfficeBuilding,
  HiOutlineCog,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineLogout,
  HiOutlineChartBar,
  HiOutlineSupport,
  HiOutlineCalendar,
  HiOutlineQuestionMarkCircle,
} from 'react-icons/hi';
import logo from '../../assets/logo.png';

const navItems = [
  { path: '/partner/dashboard', label: 'Dashboard', icon: HiOutlineViewGrid },
  { path: '/partner/organizations', label: 'Organizations', icon: HiOutlineOfficeBuilding },
  { path: '/partner/pipeline', label: 'Pipeline of Progress', icon: HiOutlineChartBar },
  { path: '/partner/schedule-meeting', label: 'Schedule Meeting', icon: HiOutlineCalendar },
  { path: '/partner/support', label: 'Support', icon: HiOutlineSupport },
  { path: '/partner/help', label: 'Help', icon: HiOutlineQuestionMarkCircle },
  { path: '/partner/settings', label: 'Settings', icon: HiOutlineCog },
];

const PartnerSidebar = ({ isOpen, onClose }) => {
  const { partner, logout } = useChannelPartnerAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/partner/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 z-50 top-0 h-screen w-64
          bg-gradient-to-b from-white to-secondary-50/30 border-r border-secondary-200/50
          flex flex-col shadow-xl lg:shadow-none
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="relative flex items-center justify-center px-3 py-4 border-b border-secondary-100/50">
          <img
            src={logo}
            alt="TeleValley"
            className="brand-logo w-full h-auto max-h-14 object-contain"
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-xl hover:bg-secondary-100 lg:hidden transition-colors"
            aria-label="Close sidebar"
          >
            <HiOutlineX className="w-5 h-5 text-secondary-400" />
          </button>
        </div>

        {/* Partner Info */}
        <div className="px-3 py-3">
          <div className="px-3 py-3 rounded-xl bg-gradient-to-r from-primary-50 to-indigo-50 border border-primary-100/50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-white">
                <span className="text-white font-semibold text-sm">
                  {partner?.name?.charAt(0) || 'P'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-secondary-900 text-sm truncate">{partner?.name}</p>
                <p className="text-xs text-secondary-500 truncate">{partner?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2">
            Menu
          </p>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${active
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25'
                        : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                      }
                    `}
                  >
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/30 rounded-r-full" />
                    )}
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-secondary-400'}`} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-secondary-100/50">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-secondary-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-600 transition-all duration-200"
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const PartnerMobileHeader = ({ onMenuClick }) => {
  const { partner } = useChannelPartnerAuth();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-secondary-200/50 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-xl text-secondary-600 hover:bg-secondary-100 transition-colors"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="brand-logo-box w-8 h-8 rounded-xl overflow-hidden shadow-md flex items-center justify-center">
            <img src={logo} alt="TeleValley" className="w-full h-full object-contain" />
          </div>
          <span className="font-bold text-secondary-900">TeleValley Partner</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center ring-2 ring-white shadow-sm">
            <span className="text-primary-600 font-semibold text-xs">
              {partner?.name?.charAt(0) || 'P'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default PartnerSidebar;
